import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { activeBirthEvents, instant } from './birthCalculations';
import { eventHeads } from './projection';
import { ClinicalTimeField, localDateTime, toOccurredAt } from './ClinicalEventEntry';
import type { PostpartumDyadProps } from './PostpartumDyad';
import type { ClinicalEvent } from './types';
export interface DischargeConfiguration {
  maternal_items: { id: string; label: string }[];
  newborn_items: { id: string; label: string }[];
}
export function dischargeEvidence(
  events: ClinicalEvent[],
  motherId: string,
  newbornIds: string[],
  config: DischargeConfiguration,
  version: number,
  cutoff?: string,
) {
  const heads = eventHeads(events);
  const pairs = [
    ...config.maternal_items.map((item) => ({ subjectId: motherId, ...item })),
    ...newbornIds.flatMap((subjectId) => config.newborn_items.map((item) => ({ subjectId, ...item }))),
  ];
  return pairs.map((item) => {
    const latest = heads
      .filter(
        (e) =>
          e.subject_id === item.subjectId &&
          e.event_type === 'CHECKLIST.ITEM' &&
          e.payload.item_id === item.id &&
          e.payload.template_version === version,
      )
      .sort((a, b) => b.sequence - a.sequence)[0];
    const ceiling = cutoff && instant(cutoff) !== null ? Math.min(instant(cutoff)!, Date.now()) : null;
    const complete =
      latest?.status === 'ACTIVE' &&
      (!cutoff ||
        (instant(latest.occurred_at) !== null &&
          instant(cutoff) !== null &&
          instant(latest.occurred_at)! <= ceiling!)) &&
      ['COMPLETE', 'NOT_APPLICABLE'].includes(String(latest.payload.status));
    return { ...item, complete, event: latest };
  });
}
export function BirthDischarge({
  episode,
  events,
  encounterId,
  timeZone,
  configuration,
  disabled,
  onSave,
}: PostpartumDyadProps) {
  const [timeEdited, setTimeEdited] = useState(false);
  const [note, setNote] = useState(''),
    [override, setOverride] = useState(''),
    [error, setError] = useState(''),
    [saving, setSaving] = useState(false),
    [time, setTime] = useState(() => localDateTime(new Date(), timeZone));
  const scoped = events.filter((e) => e.encounter_id === encounterId);
  const current = activeBirthEvents(scoped, episode.id, new Date().toISOString()) as ClinicalEvent[];
  const births = current.filter((e) => e.event_type === 'BIRTH');
  const mother = episode.subjects.find((s) => s.subject_type === 'MOTHER');
  const babies = episode.subjects.filter(
    (s) =>
      s.subject_type === 'NEWBORN' &&
      births.some((b) => b.subject_id === s.fetus_subject_id && b.payload.outcome === 'Live birth'),
  );
  const config = configuration?.clinically_reviewed ? configuration.birth_discharge : undefined;
  let cutoff: string | undefined;
  try {
    cutoff = toOccurredAt(time, timeZone);
  } catch {
    cutoff = 'invalid';
  }
  const checks = mother
    ? dischargeEvidence(
        scoped,
        mother.id,
        babies.map((s) => s.id),
        config ?? { maternal_items: [], newborn_items: [] },
        configuration?.version ?? 0,
        cutoff,
      )
    : [];
  const existing = current.find((e) => e.event_type === 'BIRTH.DISCHARGE');
  async function mark(subjectId: string, id: string) {
    if (disabled || saving) return;
    setSaving(true);
    setError('');
    try {
      await onSave(
        [
          {
            event_type: 'CHECKLIST.ITEM',
            subject_id: subjectId,
            payload: { item_id: id, template_version: configuration!.version, status: 'COMPLETE' },
          },
        ],
        new Date().toISOString(),
      );
      if (!timeEdited) setTime(localDateTime(new Date(), timeZone));
    } catch {
      setError('Checklist entry could not be saved. Retry when ready.');
    } finally {
      setSaving(false);
    }
  }
  async function discharge() {
    if (disabled || saving || !mother) return;
    if (!configuration?.version) {
      setError('Publish the practice configuration before recording birth-site discharge.');
      return;
    }
    const submittedAt = timeEdited ? cutoff : new Date().toISOString();
    if (!submittedAt || instant(submittedAt) === null) {
      setError('Enter a valid discharge time.');
      return;
    }
    if (instant(submittedAt)! > Date.now()) {
      setError('Discharge time cannot be in the future.');
      return;
    }
    if (births.some((b) => instant(b.occurred_at)! > instant(submittedAt)!) && !override.trim()) {
      setError('Discharge precedes documented birth evidence. Correct the time or enter an explicit review override.');
      return;
    }
    if (!note.trim()) {
      setError('Enter a discharge note.');
      return;
    }
    const submittedChecks = dischargeEvidence(
      scoped,
      mother.id,
      babies.map((s) => s.id),
      config ?? { maternal_items: [], newborn_items: [] },
      configuration.version,
      submittedAt,
    );
    if ((!config || !births.length || submittedChecks.some((c) => !c.complete)) && !override.trim()) {
      setError('Complete the reviewed mother and newborn checklists, or enter an explicit override reason.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(
        [
          {
            event_type: 'BIRTH.DISCHARGE',
            subject_id: mother.id,
            payload: {
              text: note.trim(),
              checklist_version: configuration?.version ?? 0,
              reviewed_event_ids: [
                ...births.map((b) => b.id),
                ...submittedChecks.filter((c) => c.complete).map((c) => c.event!.id),
              ].sort(),
              ...(override.trim() ? { override_reason: override.trim() } : {}),
            },
          },
        ],
        submittedAt,
      );
      setNote('');
      setOverride('');
    } catch {
      setError(
        'Discharge was not accepted. Your note is retained. Synchronize and review the latest birth and checklist evidence before retrying.',
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Discharge from birth site</CardTitle>
        <CardDescription>
          Review the mother and each baby before documenting discharge. This does not schedule follow-up visits.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {existing ? (
          <Alert>
            <AlertDescription>
              Discharge is documented. Use event history to correct the existing record.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {!configuration?.version && (
              <Alert>
                <AlertDescription>
                  Publish the practice configuration before recording birth-site discharge.
                </AlertDescription>
              </Alert>
            )}
            {!!configuration?.version && !config && (
              <Alert>
                <AlertDescription>
                  No reviewed discharge checklist is configured. An explicit override reason is required.
                </AlertDescription>
              </Alert>
            )}
            {checks.map((c) => (
              <div key={`${c.subjectId}:${c.id}`} className="flex items-center justify-between gap-4">
                <span>
                  {episode.subjects.find((s) => s.id === c.subjectId)?.label}: {c.label}
                </span>
                <Button
                  className="min-h-11"
                  variant="outline"
                  disabled={disabled || saving || c.complete}
                  onClick={() => mark(c.subjectId, c.id)}>
                  {c.complete ? 'Complete' : 'Record completed'}
                </Button>
              </div>
            ))}
            <FieldGroup>
              <ClinicalTimeField
                value={time}
                onChange={(v) => {
                  setTime(v);
                  setTimeEdited(true);
                }}
                timeZone={timeZone}
                disabled={disabled || saving}
              />
              <p className="text-sm text-muted-foreground">
                Unedited discharge time uses the time you save; a manually chosen time is preserved.
              </p>
              <Field>
                <FieldLabel>Discharge note</FieldLabel>
                <Textarea
                  aria-label="Discharge note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={disabled || saving}
                />
              </Field>
              <Field>
                <FieldLabel>Override reason (if any required evidence is incomplete)</FieldLabel>
                <Textarea
                  aria-label="Discharge override reason"
                  value={override}
                  onChange={(e) => setOverride(e.target.value)}
                  disabled={disabled || saving}
                />
              </Field>
            </FieldGroup>
            <Button className="min-h-11" onClick={discharge} disabled={disabled || saving || !configuration?.version}>
              {saving ? 'Saving…' : 'Record reviewed discharge'}
            </Button>
          </>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
